import React, { useEffect, useRef, useState } from 'react';
import PermissionLink from './PermissionLink';
import {
    CapType,
    CapTypeDevice,
    listCapTypes,
    listCapTypeDevices,
} from '../api/captype';
import { CapRecState } from '../api/material';
import { useCan } from '../store';
import { useDictText } from './DictSelect';
import Modal from './Modal';
import Switch from './Switch';

// ---- 共享 hook: 工艺类型列表 (模块级缓存, 全站各处徽标共用一次请求) ------

let capTypesCache: CapType[] | null = null;
let capTypesPromise: Promise<CapType[]> | null = null;

function fetchCapTypesCached(): Promise<CapType[]> {
    if (capTypesCache) return Promise.resolve(capTypesCache);
    if (!capTypesPromise) {
        capTypesPromise = listCapTypes()
            .then((list) => {
                capTypesCache = list || [];
                return capTypesCache;
            })
            .catch((e) => {
                capTypesPromise = null;
                throw e;
            });
    }
    return capTypesPromise;
}

// 已挂载的 useCapTypes 订阅者: 缓存失效时通知它们立刻重拉 (设备详情里改绑定后卡片即时刷新, 不用等重新挂载)
const capTypesListeners = new Set<() => void>();

/** 工艺类型增删改 (含设备绑定) 后调用: 清模块缓存并让所有已挂载的 useCapTypes 重新拉取. */
export function invalidateCapTypesCache() {
    capTypesCache = null;
    capTypesPromise = null;
    capTypesListeners.forEach((fn) => fn());
}

/** 全局工艺类型定义 (替代原硬编码 CAP_DEFS), 按 sort 升序. 缓存失效时自动重拉. */
export function useCapTypes(): { capTypes: CapType[]; loaded: boolean } {
    const [capTypes, setCapTypes] = useState<CapType[]>(capTypesCache || []);
    const [loaded, setLoaded] = useState(capTypesCache !== null);
    useEffect(() => {
        let cancelled = false;
        const load = () => {
            fetchCapTypesCached()
                .then((list) => {
                    if (!cancelled) {
                        setCapTypes([...list].sort((a, b) => a.sort - b.sort));
                        setLoaded(true);
                    }
                })
                .catch(() => {
                    if (!cancelled) setLoaded(true);
                });
        };
        load();
        capTypesListeners.add(load);
        return () => {
            cancelled = true;
            capTypesListeners.delete(load);
        };
    }, []);
    return { capTypes, loaded };
}

// ---- 设备行列表 (工艺类型 / 方案类型 等「具备某能力的设备」弹窗共用) ----------

export interface DeviceRowItem {
    deviceKey: string;
    /** 设备名称 (展示用, 空则回退 deviceKey) */
    name?: string;
    family?: string;
    archCode?: string;
    stdW?: number;
}

/** 设备行: 整行可点, 跳设备工况页并选中该台 (?deviceKey= 深链, 见 DeviceTopSelector). tag 显示在行右侧. */
export function DeviceRows({
    devices,
    tag,
    onNavigate,
    disabledKeys,
    onToggleDisabled,
}: {
    devices: DeviceRowItem[];
    tag?: string;
    onNavigate: () => void;
    /** 传了 onToggleDisabled 时, 每行右侧多一个「禁用」开关; disabledKeys 为当前被禁用的 deviceKey */
    disabledKeys?: string[];
    onToggleDisabled?: (deviceKey: string, disabled: boolean) => void;
}) {
    return (
        <div className="space-y-2">
            {devices.map((d) => (
                <PermissionLink
                    resource="device"
                    hide
                    key={d.deviceKey}
                    to={`/devices?deviceKey=${encodeURIComponent(d.deviceKey)}`}
                    onClick={onNavigate}
                    title={`查看 ${d.deviceKey} 的设备工况`}
                    className="flex items-center justify-between border border-slate-100 rounded-lg px-3 py-2 hover:border-brand-200 hover:bg-brand-50/40 transition-colors group"
                >
                    <div className="flex items-center gap-2">
                        <span className="w-8 h-8 rounded bg-brand-50 grid place-items-center">
                            🖨️
                        </span>
                        <div>
                            <div className="text-sm font-semibold group-hover:text-brand-700">
                                {d.name || d.deviceKey}
                            </div>
                            <div className="text-[11px] text-slate-400">
                                {d.family || '—'}
                                {d.archCode ? ` · ${d.archCode}` : ''}
                                {d.stdW ? ` · ${d.stdW}W` : ''}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {onToggleDisabled && (
                            <label
                                className="flex items-center gap-1.5 text-xs text-slate-500"
                                title="仅对当前材料在该工艺类型下生效"
                            >
                                禁用
                                <Switch
                                    tone="danger"
                                    on={(disabledKeys || []).includes(
                                        d.deviceKey,
                                    )}
                                    onChange={(next) =>
                                        onToggleDisabled(d.deviceKey, next)
                                    }
                                />
                            </label>
                        )}
                        {tag && (
                            <span className="tag bg-brand-50 text-brand-700">
                                {tag}
                            </span>
                        )}
                        <span className="text-brand-400 opacity-0 group-hover:opacity-100 transition-opacity">
                            →
                        </span>
                    </div>
                </PermissionLink>
            ))}
        </div>
    );
}

// ---- 弹窗: 具备「XX」的设备 · N 台 --------------------------------------

export function CapDevicesModal({
    cap,
    onClose,
    extraFooter,
    disabledKeys,
    onToggleDisabled,
}: {
    /** 为 null 时不显示 */
    cap: CapType | null;
    onClose: () => void;
    /** 可选的底部附加区 (如材料详情页的 启用/停用 开关) */
    extraFooter?: React.ReactNode;
    /** 材料详情编辑态: 当前材料在该工艺类型下被禁用的设备, 及切换回调 (不传则不显示开关) */
    disabledKeys?: string[];
    onToggleDisabled?: (deviceKey: string, disabled: boolean) => void;
}) {
    const [devices, setDevices] = useState<CapTypeDevice[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    // 光源/动作展示名来自字典维护 (light_source / cap_action), 未命中回退原始 code
    const lightText = useDictText('light_source')(cap?.lightSource);
    const actionText = useDictText('cap_action')(cap?.action);

    const capKey = cap?.capKey || '';
    useEffect(() => {
        if (!capKey) return;
        let cancelled = false;
        setLoading(true);
        setError('');
        setDevices([]);
        listCapTypeDevices(capKey)
            .then((list) => {
                if (!cancelled) setDevices(list || []);
            })
            .catch((e) => {
                if (!cancelled) setError(String(e));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [capKey]);

    if (!cap) return null;
    return (
        <Modal
            open
            width={560}
            title={`具备「${cap.label}」的设备 · ${devices.length} 台`}
            onClose={onClose}
        >
            <div className="p-5">
                <div className="text-xs text-slate-500 mb-3">
                    工艺类型显式绑定的设备 (仅启用中的); 绑定在「数据字典 ·
                    工艺类型」里维护。
                    {cap.lightSource || cap.action
                        ? ` 光源 ${lightText} · 动作 ${actionText}`
                        : ''}
                </div>
                {loading && (
                    <div className="text-sm text-slate-400 py-6 text-center">
                        加载中…
                    </div>
                )}
                {error && !loading && (
                    <div className="text-sm text-rose-600 py-4 text-center">
                        加载失败: {error}
                    </div>
                )}
                {!loading && !error && devices.length === 0 && (
                    <div className="text-center text-slate-400 text-sm py-8">
                        暂无具备该能力的设备
                    </div>
                )}
                {!loading && !error && devices.length > 0 && (
                    <DeviceRows
                        devices={devices}
                        tag={
                            cap.lightSource || cap.action
                                ? `${lightText} · ${actionText}`
                                : undefined
                        }
                        onNavigate={onClose}
                        disabledKeys={disabledKeys}
                        onToggleDisabled={onToggleDisabled}
                    />
                )}
                {extraFooter}
                <div className="flex justify-end mt-4">
                    <button
                        type="button"
                        className="btn-ghost"
                        onClick={onClose}
                    >
                        关闭
                    </button>
                </div>
            </div>
        </Modal>
    );
}

// ---- 可点击的工艺类型徽标 ------------------------------------------------

// 推荐状态 → 徽标样式与前缀: 推荐 ✓ 绿 / 不推荐 ✕ 红 / 未设置 · 灰
const CAP_STATE_LOOK: Record<CapRecState, { cls: string; mark: string }> = {
    rec: { cls: 'cap-on', mark: '✓' },
    not: { cls: 'bg-rose-50 text-rose-600', mark: '✕' },
    none: { cls: 'cap-off', mark: '·' },
};

export function ClickableCapBadge({
    cap,
    enabled,
    state,
    prefix,
    onOpen,
}: {
    cap: CapType;
    /** 老口径: 启用 = 推荐; 传了 state 时以 state 为准 */
    enabled?: boolean;
    /** 推荐状态 (材料适配工艺类型, 2026-10-28) */
    state?: CapRecState;
    /** 徽标前缀 (如推荐序号), 不传用状态符号 */
    prefix?: React.ReactNode;
    onOpen: (cap: CapType) => void;
}) {
    const look = CAP_STATE_LOOK[state ?? (enabled ? 'rec' : 'none')];
    // 弹层内容是设备清单 (device 域): 没有设备查看权的人只看徽标, 不给点
    const canSeeDevices = useCan('device', 'get');
    if (!canSeeDevices) {
        return (
            <span className={`tag ${look.cls}`}>
                {prefix ?? look.mark} {cap.label}
            </span>
        );
    }
    return (
        <button
            type="button"
            className={`tag ${look.cls} cursor-pointer hover:opacity-80`}
            title={`查看具备「${cap.label}」的设备`}
            onClick={(e) => {
                e.stopPropagation();
                onOpen(cap);
            }}
        >
            {prefix ?? look.mark} {cap.label}
        </button>
    );
}

/**
 * 列表单元格: 按启用的 capKey 数组渲染徽标 (最多 limit 个, 其余折叠 +n),
 * 点击任一徽标弹出"具备该能力的设备"弹窗. 自带弹窗状态, 各页直接使用.
 */
export function CapBadgesCell({
    caps,
    limit = 4,
    nowrap = false,
}: {
    caps: string[];
    limit?: number;
    /** 单行不换行 (与相邻列逐行对齐的场景, 配合小 limit 使用) */
    nowrap?: boolean;
}) {
    const { capTypes } = useCapTypes();
    const [openCap, setOpenCap] = useState<CapType | null>(null);
    // +N 悬停展开: 记录锚点位置, fixed 定位弹层 (表格容器 overflow-hidden 会裁 absolute)
    const [moreRect, setMoreRect] = useState<DOMRect | null>(null);
    const hideTimer = useRef<ReturnType<typeof setTimeout>>();
    const enabled = capTypes.filter((c) => caps.includes(c.capKey));
    if (caps.length === 0) {
        return <span className="text-xs text-slate-300">—</span>;
    }
    // capTypes 还没加载完时先用原 key 兜底展示
    const shownCaps: CapType[] =
        enabled.length > 0
            ? enabled
            : caps.map((k) => ({
                  capKey: k,
                  label: k,
                  lightSource: '',
                  action: '',
                  sort: 0,
                  deviceKeys: [],
              }));
    const shown = shownCaps.slice(0, limit);
    const hidden = shownCaps.slice(limit);
    const keepOpen = () => clearTimeout(hideTimer.current);
    const delayClose = () => {
        hideTimer.current = setTimeout(() => setMoreRect(null), 200);
    };
    return (
        <>
            <div
                className={`flex gap-1 ${nowrap ? 'flex-nowrap' : 'flex-wrap'}`}
            >
                {shown.map((c) => (
                    <ClickableCapBadge
                        key={c.capKey}
                        cap={c}
                        enabled
                        onOpen={setOpenCap}
                    />
                ))}
                {hidden.length > 0 && (
                    <span
                        className="tag bg-slate-100 text-slate-500 text-[10px] cursor-default"
                        onMouseEnter={(e) => {
                            keepOpen();
                            setMoreRect(
                                e.currentTarget.getBoundingClientRect(),
                            );
                        }}
                        onMouseLeave={delayClose}
                    >
                        +{hidden.length}
                    </span>
                )}
            </div>
            {moreRect && hidden.length > 0 && (
                <div
                    className="fixed z-50"
                    style={{ left: moreRect.left, top: moreRect.bottom + 4 }}
                    onMouseEnter={keepOpen}
                    onMouseLeave={delayClose}
                >
                    <div
                        className="bg-white border border-slate-200 rounded-lg shadow-xl p-2 flex flex-wrap gap-1"
                        style={{ maxWidth: 320 }}
                    >
                        {hidden.map((c) => (
                            <ClickableCapBadge
                                key={c.capKey}
                                cap={c}
                                enabled
                                onOpen={(cap) => {
                                    setMoreRect(null);
                                    setOpenCap(cap);
                                }}
                            />
                        ))}
                    </div>
                </div>
            )}
            <CapDevicesModal cap={openCap} onClose={() => setOpenCap(null)} />
        </>
    );
}
